import React from 'react';
import ScoreBagsDisplay from './ScoreBagsDisplay';
import { formatLossLimit } from '../utils/spadesSettingsForm';

const TEAM_SEATS = {
  ns: ['north', 'south'],
  ew: ['east', 'west'],
};

const TEAM_ORDER = ['ns', 'ew'];

function occupantLabel(occ) {
  if (!occ) return '—';
  return occ.username || occ.botLabel || '?';
}

function partnerLine(seats, tableSeats) {
  const names = seats
    .map((seat) => occupantLabel(tableSeats?.[seat]))
    .filter((n) => n !== '—');
  return names.length ? names.join(' · ') : '—';
}

function SpadesScoreboard({
  scoreboard,
  settings,
  seats,
  compact = false,
  variant = 'panel',
}) {
  const scoringMode = scoreboard?.scoringMode || settings?.scoringMode || 'teams';
  const rootClass = [
    'spades-scoreboard',
    compact ? 'spades-scoreboard--compact' : '',
    variant === 'on-table' ? 'spades-scoreboard--on-table' : '',
    variant === 'dock' ? 'spades-scoreboard--dock' : '',
  ].filter(Boolean).join(' ');

  if (!scoreboard || !settings) {
    return (
      <div className={`${rootClass} spades-scoreboard--empty`}>
        {!compact && <h3>Scoreboard</h3>}
        <p className="spades-scoreboard-hint">
          {settings
            ? `Goal ${settings.winAt} · ${settings.bagLimit} bags (−${settings.bagPenalty})`
            : 'Scores appear when a game starts'}
        </p>
      </div>
    );
  }

  const { teams, hand } = scoreboard;

  if (scoringMode === 'singles') {
    return (
      <div className={rootClass}>
        <h3>{compact ? `H${hand || 0}` : `Spades · Hand ${hand || 0}`}</h3>
        {!compact && <p className="spades-scoreboard-mode">Singles</p>}
        <ul className="spades-scoreboard-singles">
          {['north', 'south', 'east', 'west'].map((seat) => {
            const occ = seats?.[seat];
            if (!occ) return null;
            const ps = scoreboard.players?.[seat];
            return (
              <li key={seat}>
                <span>{occupantLabel(occ)}</span>
                <ScoreBagsDisplay score={ps?.score ?? 0} bags={ps?.bags ?? 0} />
              </li>
            );
          })}
        </ul>
        {!compact && (
          <p className="spades-scoreboard-rules">
            Goal {settings.winAt} · Set {formatLossLimit(settings.autoLossAt)}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className={rootClass}>
      <h3>{compact ? `H${hand || 0}` : `Spades · Hand ${hand || 0}`}</h3>
      {!compact && <p className="spades-scoreboard-mode">Teams</p>}
      <div className="spades-score-teams-unified">
        {TEAM_ORDER.map((teamKey, idx) => {
          const teamSeats = TEAM_SEATS[teamKey];
          const occ = teamSeats.some((s) => seats?.[s]);
          if (!occ) return null;
          const short = teamKey === 'ns' ? 'N/S' : 'E/W';
          // Dock (desktop window) prefers real names; tiny on-felt chips stay N/S E/W
          const label = (compact && variant !== 'dock')
            ? short
            : partnerLine(teamSeats, seats);
          return (
            <React.Fragment key={teamKey}>
              {idx > 0 && <div className="spades-score-divider" aria-hidden="true" />}
              <div className="spades-score-row">
                <span
                  className="spades-score-partners"
                  title={teamKey === 'ns' ? 'North & South' : 'East & West'}
                >
                  {label}
                </span>
                <ScoreBagsDisplay
                  score={teams?.[teamKey]?.score ?? 0}
                  bags={teams?.[teamKey]?.bags ?? 0}
                />
              </div>
            </React.Fragment>
          );
        })}
      </div>
      {!compact && (
        <p className="spades-scoreboard-rules">
          Goal {settings.winAt} · Set {formatLossLimit(settings.autoLossAt)}
        </p>
      )}
    </div>
  );
}

export default SpadesScoreboard;